import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";

const HorizontalStatusBarChart = ({
  api,
  queryName,
  keyName,
  title = "Title Name",
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [chartData, setChartData] = useState({ categories: [], series: [] });

  const getValueByKey = (obj, key) =>
    key.split(".").reduce((o, k) => o?.[k], obj);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  const loadData = async () => {
    try {
      const res = await api({ queryName, page: 1, per_page: 1000 });
      const rows = res?.data?.data || res?.data || [];
      if (!rows.length) return;

      const countMap = rows.reduce((acc, row) => {
        const value = getValueByKey(row, keyName);
        if (!value) return acc;
        acc[value] = (acc[value] || 0) + 1;
        return acc;
      }, {});

      const sorted = Object.entries(countMap).sort((a, b) => b[1] - a[1]);

      setChartData({
        categories: sorted.map(([k]) => k),
        series: [{ name: "Count", data: sorted.map(([, v]) => v) }],
      });
    } catch (err) {
      console.error("Chart Error:", err);
    }
  };

  // ✅ Label moves WITH the bar tip, counts 0 → target, then fades out
  const runCounterAnimation = (values, maxVal, duration = 1500) => {
    const container = chartRef.current;
    if (!container) return;

    const old = container.querySelector(".count-overlay");
    if (old) old.remove();

    const overlay = document.createElement("div");
    overlay.className = "count-overlay";
    overlay.style.cssText = `
      position: absolute;
      top: 0; left: 0;
      width: 100%; height: 100%;
      pointer-events: none;
      z-index: 10;
    `;
    container.style.position = "relative";
    container.appendChild(overlay);
    const svgEl = container.querySelector("svg");
    const innerRect = container.querySelector(".apexcharts-inner");

    // Fallback dimensions if refs not ready
    const totalWidth = svgEl?.clientWidth || container.clientWidth;
    const plotLeft = innerRect?.getBoundingClientRect().left
      - container.getBoundingClientRect().left || totalWidth * 0.22;
    const plotWidth = innerRect?.clientWidth || totalWidth * 0.72;

    const barHeight = 52;
    const chartTopOffset = 60;

    values.forEach((target, i) => {
      const label = document.createElement("div");
      label.style.cssText = `
        position: absolute;
        top: ${chartTopOffset + i * barHeight + barHeight / 2 - 10}px;
        left: ${plotLeft}px;
        font-size: 13px;
        font-weight: 700;
        color: #000000;
        transition: opacity 0.4s ease;
        opacity: 1;
        white-space: nowrap;
      `;
      overlay.appendChild(label);

      let start = null;

      const step = (timestamp) => {
        if (!start) start = timestamp;
        const progress = Math.min((timestamp - start) / duration, 1);

        // ✅ easeInOut matches ApexCharts animation curve
        const ease =
          progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        const current = Math.floor(ease * target);
        label.textContent = current;

        // ✅ Move label horizontally with bar tip
        // barTipX = plotLeft + (current / maxVal) * plotWidth
        const barTipX = plotLeft + (ease * target / maxVal) * plotWidth;
        label.style.left = `${barTipX + 6}px`; // 6px gap after tip

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          label.textContent = target;
          label.style.left = `${plotLeft + (target / maxVal) * plotWidth + 6}px`;

          // ✅ Fade out after reaching final value
          setTimeout(() => {
            label.style.opacity = "0";
            setTimeout(() => overlay.remove(), 450);
          }, 200);
        }
      };

      requestAnimationFrame(step);
    });
  };

  useEffect(() => {
    if (!chartRef.current || !chartData.series.length) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    const values = chartData.series[0]?.data || [];
    const maxVal = Math.max(...values, 1);

    const chart = new ApexCharts(chartRef.current, {
      series: chartData.series,
      chart: {
        type: "bar",
        height: Math.max(300, chartData.categories.length * 52 + 80),
        toolbar: { show: true },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 1500,
          animateGradually: { enabled: false },
          dynamicAnimation: { enabled: true, speed: 1500 },
        },
        events: {
          mounted: () => {
            // ✅ slight delay so SVG is fully in DOM before measuring
            setTimeout(() => {
              runCounterAnimation(values, maxVal, 1500);
            }, 50);
          },
        },
      },
      title: {
        text: title,
        align: "left",
        style: { fontSize: "16px", fontWeight: 600 },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "65%",
          borderRadius: 6,
        },
      },
      dataLabels: {
        enabled: false, // ✅ custom overlay handles labels
      },
      grid: {
        borderColor: "#e5e7eb",
        strokeDashArray: 4,
        padding: { left: 20, right: 50 },
      },
      colors: ["#2ea0ff"],
      xaxis: {
        categories: chartData.categories,
        min: 0,
        max: maxVal + Math.ceil(maxVal * 0.18),
        tickAmount: 6,
        labels: { style: { fontSize: "12px" } },
      },
      yaxis: {
        type: "category",
        labels: {
          style: { fontSize: "13px" },
          maxWidth: 160,
        },
      },
      tooltip: {
        y: { formatter: (val) => `${val}` },
      },
    });

    chart.render();
    chartInstanceRef.current = chart;

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, title]);

  return <div ref={chartRef} />;
};

export default HorizontalStatusBarChart;
